import { assertExists } from '@blocksuite/global/utils';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import type { Ref } from 'react';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTransition } from 'react-transition-state';

import * as styles from './resize-panel.css';

export interface ResizeHandleProps
  extends React.HtmlHTMLAttributes<HTMLDivElement> {
  resizing: boolean;
  open: boolean;
  minWidth: number;
  maxWidth: number;
  resizeHandlePos: 'left' | 'right';
  resizeHandleOffset?: number;
  resizeHandleVerticalPadding?: number;
  onOpen: (open: boolean) => void;
  onResizing: (resizing: boolean) => void;
  onWidthChange: (width: number) => void;
}

export interface ResizePanelProps
  extends React.HtmlHTMLAttributes<HTMLDivElement> {
  resizing: boolean;
  open: boolean;
  floating?: boolean;
  minWidth: number;
  maxWidth: number;
  resizeHandlePos: 'left' | 'right';
  resizeHandleOffset?: number;
  resizeHandleVerticalPadding?: number;
  enableAnimation?: boolean;
  width: number;
  unmountOnExit?: boolean;

  // New optional props for dual-edge handles
  enableDualHandles?: boolean;
  allowGrowBeyondMax?: boolean;

  // New optional props for height resizing
  enableHeightResize?: boolean;
  height?: number;
  minHeight?: number;
  maxHeight?: number;
  allowGrowBeyondMaxHeight?: boolean;
  onHeightChange?: (height: number) => void;

  onOpen: (open: boolean) => void;
  onResizing: (resizing: boolean) => void;
  onWidthChange: (width: number) => void;
}

const ResizeHandle = memo(function ResizeHandle({
  className,
  resizing,
  minWidth,
  maxWidth,
  resizeHandlePos,
  resizeHandleOffset,
  resizeHandleVerticalPadding,
  open,
  onOpen,
  onResizing,
  onWidthChange,
  ...rest
}: ResizeHandleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onResizeStart = useCallback(() => {
    let resized = false;
    const panelContainer = ref.current?.parentElement;
    assertExists(
      panelContainer,
      'parent element not found for resize indicator'
    );

    // Prevent text selection during drag (mirror CustomResizeHandle behavior)
    const originalSelectStart = document.onselectstart;
    const originalUserSelect = document.body.style.userSelect;
    document.onselectstart = () => false;
    document.body.style.userSelect = 'none';

    const { left: anchorLeft, right: anchorRight } =
      panelContainer.getBoundingClientRect();

    function onMouseMove(e: MouseEvent) {
      e.preventDefault();
      if (!panelContainer) return;
      const newWidth = Math.min(
        maxWidth,
        Math.max(
          resizeHandlePos === 'right'
            ? e.clientX - anchorLeft
            : anchorRight - e.clientX,
          minWidth
        )
      );
      onWidthChange(newWidth);
      onResizing(true);
      resized = true;
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener(
      'mouseup',
      () => {
        // if not resized, toggle sidebar
        if (!resized) {
          onOpen(false);
        }
        onResizing(false);
        // Restore text selection
        document.onselectstart = originalSelectStart;
        document.body.style.userSelect = originalUserSelect;
        document.removeEventListener('mousemove', onMouseMove);
      },
      { once: true }
    );
  }, [maxWidth, resizeHandlePos, minWidth, onWidthChange, onResizing, onOpen]);

  return (
    <div
      {...rest}
      data-testid="resize-handle"
      ref={ref}
      style={assignInlineVars({
        [styles.resizeHandleOffsetVar]: `${resizeHandleOffset ?? 0}px`,
        [styles.resizeHandleVerticalPadding]: `${resizeHandleVerticalPadding ?? 0
          }px`,
      })}
      className={clsx(styles.resizeHandleContainer, className)}
      data-handle-position={resizeHandlePos}
      data-resizing={resizing}
      data-open={open}
      onMouseDown={onResizeStart}
    >
      <div className={styles.resizerInner} />
    </div>
  );
});

interface CustomResizeHandleProps {
  side: 'left' | 'right' | 'top';
  resizing: boolean;
  minWidth: number;
  maxWidth: number;
  allowGrowBeyondMax?: boolean;
  minHeight?: number;
  maxHeight?: number;
  allowGrowBeyondMaxHeight?: boolean;
  onResizing: (resizing: boolean) => void;
  onWidthChange: (width: number) => void;
  onHeightChange?: (height: number) => void;
}

const CustomResizeHandle = memo(function CustomResizeHandle({
  side,
  resizing,
  minWidth,
  maxWidth,
  allowGrowBeyondMax,
  minHeight,
  maxHeight,
  allowGrowBeyondMaxHeight,
  onResizing,
  onWidthChange,
  onHeightChange,
}: CustomResizeHandleProps) {
  const ref = useRef<HTMLDivElement>(null);

  const onResizeStart = useCallback(() => {
    const panelContainer = ref.current?.parentElement;
    assertExists(panelContainer, 'parent element not found for custom resize handle');

    // Prevent text selection during drag
    const originalSelectStart = document.onselectstart;
    const originalUserSelect = document.body.style.userSelect;
    document.onselectstart = () => false;
    document.body.style.userSelect = 'none';

    function onMouseMove(e: MouseEvent) {
      e.preventDefault();
      if (!panelContainer) return;

      if (side === 'top') {
        // Height resizing from top edge
        const rect = panelContainer.getBoundingClientRect();
        const newHeight = rect.bottom - e.clientY;

        const effectiveMaxHeight = allowGrowBeyondMaxHeight ? Infinity : (maxHeight || Infinity);
        const constrainedHeight = Math.min(effectiveMaxHeight, Math.max(newHeight, minHeight || 0));

        onHeightChange?.(constrainedHeight);
        onResizing(true);
      } else {
        // Width resizing from left/right edge  
        const rect = panelContainer.getBoundingClientRect();
        const newWidth = side === 'right'
          ? e.clientX - rect.left
          : rect.right - e.clientX;

        const effectiveMaxWidth = allowGrowBeyondMax ? Infinity : maxWidth;
        const constrainedWidth = Math.min(effectiveMaxWidth, Math.max(newWidth, minWidth));

        onWidthChange(constrainedWidth);
        onResizing(true);
      }
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener(
      'mouseup',
      () => {
        onResizing(false);
        // Restore text selection
        document.onselectstart = originalSelectStart;
        document.body.style.userSelect = originalUserSelect;
        document.removeEventListener('mousemove', onMouseMove);
      },
      { once: true }
    );
  }, [side, minWidth, maxWidth, allowGrowBeyondMax, minHeight, maxHeight, allowGrowBeyondMaxHeight, onWidthChange, onHeightChange, onResizing]);

  return (
    <div
      ref={ref}
      className={styles.customResizeHandle}
      data-side={side}
      data-resizing={resizing}
      onMouseDown={onResizeStart}
    />
  );
});

// delay initial animation to avoid flickering
function useEnableAnimation() {
  const [enable, setEnable] = useState(false);
  useEffect(() => {
    window.setTimeout(() => {
      setEnable(true);
    }, 500);
  }, []);
  return enable;
}

const animationTimeout = 300;

export function ResizePanel({
  ref,
  children,
  className,
  resizing,
  minWidth,
  maxWidth,
  width,
  floating,
  enableAnimation: _enableAnimation = true,
  open,
  unmountOnExit,
  onOpen,
  onResizing,
  onWidthChange,
  resizeHandlePos,
  resizeHandleOffset,
  resizeHandleVerticalPadding,
  enableDualHandles,
  allowGrowBeyondMax,
  enableHeightResize,
  height,
  minHeight,
  maxHeight,
  allowGrowBeyondMaxHeight,
  onHeightChange,
  ...rest
}: ResizePanelProps & { ref?: Ref<HTMLDivElement> }) {
    const enableAnimation = useEnableAnimation() && _enableAnimation;
    // Pique Update
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const safeWidth = Math.min(
      maxWidth,
      Math.max(minWidth, width > screenWidth ? screenWidth : width)
    );
    const [{ status }, toggle] = useTransition({
      timeout: animationTimeout,
    });
    useLayoutEffect(() => {
      toggle(open);
    }, [open]);
    return (
      <div
        {...rest}
        ref={ref}
        style={assignInlineVars({
          [styles.panelWidthVar]: `${safeWidth}px`,
          [styles.animationTimeout]: `${animationTimeout}ms`,
        })}
        className={clsx(className, styles.root)}
        data-open={open}
        data-transition-state={status}
        data-is-floating={floating}
        data-handle-position={resizeHandlePos}
        data-enable-animation={enableAnimation && !resizing}
      >
        {!(status === 'exited' && unmountOnExit !== false) && children}
        {!enableDualHandles && (
          <ResizeHandle
            resizeHandlePos={resizeHandlePos}
            resizeHandleOffset={resizeHandleOffset}
            resizeHandleVerticalPadding={resizeHandleVerticalPadding}
            maxWidth={maxWidth}
            minWidth={minWidth}
            onOpen={onOpen}
            onResizing={onResizing}
            onWidthChange={onWidthChange}
            open={open}
            resizing={resizing}
          />
        )}
        {enableDualHandles && (
          <>
            <CustomResizeHandle
              side="left"
              resizing={resizing}
              minWidth={minWidth}
              maxWidth={maxWidth}
              allowGrowBeyondMax={allowGrowBeyondMax}
              minHeight={minHeight}
              maxHeight={maxHeight}
              allowGrowBeyondMaxHeight={allowGrowBeyondMaxHeight}
              onResizing={onResizing}
              onWidthChange={onWidthChange}
              onHeightChange={onHeightChange}
            />
            <CustomResizeHandle
              side="right"
              resizing={resizing}
              minWidth={minWidth}
              maxWidth={maxWidth}
              allowGrowBeyondMax={allowGrowBeyondMax}
              minHeight={minHeight}
              maxHeight={maxHeight}
              allowGrowBeyondMaxHeight={allowGrowBeyondMaxHeight}
              onResizing={onResizing}
              onWidthChange={onWidthChange}
              onHeightChange={onHeightChange}
            />
          </>
        )}
        {enableHeightResize && (
          <CustomResizeHandle
            side="top"
            resizing={resizing}
            minWidth={minWidth}
            maxWidth={maxWidth}
            allowGrowBeyondMax={allowGrowBeyondMax}
            minHeight={minHeight}
            maxHeight={maxHeight}
            allowGrowBeyondMaxHeight={allowGrowBeyondMaxHeight}
            onResizing={onResizing}
            onWidthChange={onWidthChange}
            onHeightChange={onHeightChange}
          />
        )}
      </div>
    );
}
